import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Modal,
  Col,
  Button,
  Upload,
  Popconfirm,
  Descriptions,
  List,
  Tooltip,
  Spin,
} from "antd";
import {
  LeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  MessageTwoTone,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
  useGetSingleSalesRequestDataQuery,
  useEditSalesRequestMutation,
  useGetPartnersProductListQuery,
} from "../../../appRedux/api/royaltySalesApi";
import classNames from "classnames";
import { royalty_countries_product } from "../../../constants";
import moment from "moment";
import { getRole, getUserInfo, getIsApprover } from "../../../helpers";
import { useNavigate, useParams } from "react-router-dom";
const confirm = Modal.confirm;

const { TextArea } = Input;

const ViewRaisedRequest = (props) => {
  const [isAdminApprover, setIsAdminApprover] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const currentDate = new Date();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();

  // RTK Query hooks
  const { data: partnersProductList, isLoading: isProductsLoading } =
    useGetPartnersProductListQuery();
  const { data: salesRequestData, isLoading: isRequestLoading } =
    useGetSingleSalesRequestDataQuery(id);
  const [editSalesRequest, { isLoading: isEditing }] =
    useEditSalesRequestMutation();

  useEffect(() => {
    setIsAdminApprover(getIsApprover());
    setIsUserAdmin(getRole() === "ADMIN");
    setIsUserPartner(getRole() === "PARTNER");
  }, []);

  const [chatConversation, setChatConversation] = useState([]);
  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [messageInput, setMessageInput] = useState("");

  const handleMessageChange = (e) => {
    setMessageInput(e.target.value);
  };

  const handleSendMessage = (item) => {
    if (messageInput.trim() !== "") {
      const newMessage = {
        message: messageInput.trim(),
        is_partner: isUserPartner,
      };
      const formData = new FormData();
      formData.append("status", "NEEDS CLARIFICATION");
      formData.append("message", newMessage.message);

      editSalesRequest({
        id: item,
        data: formData,
        status: "Message Sent",
      });

      const updatedChatConversation = [...chatConversation, newMessage];
      setMessageInput("");
      setChatConversation(updatedChatConversation);
    }
  };

  const handleClarificationClick = (chatMessages) => {
    setChatConversation(chatMessages);
    setIsChatModalVisible(true);
  };

  const navigateBack = () => {
    navigate(-1);
  };

  const isDisabled =
    !salesRequestData ||
    (salesRequestData.status !== "PENDING" &&
      salesRequestData.status !== "NEEDS CLARIFICATION");

  const showCommentsForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>Add comments for partner:</span>
        </div>
        <Form.Item
          name="comment"
          className="mb-0"
          rules={[
            {
              required: isUserAdmin,
              message: isUserAdmin
                ? "Enter comments for partner"
                : "Enter comments for admin",
            },
          ]}
        >
          <TextArea placeholder="Enter comments here" />
        </Form.Item>
      </Form>
    );
  };

  const showApproveConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to approve the report?",
      className: "btn-confirm-green",
      content: showCommentsForm(),
      icon: <CheckCircleOutlined />,
      okText: "Approve",
      cancelText: "Cancel",
      onOk(close) {
        form.validateFields().then((values) => {
          const approveFormData = new FormData();
          approveFormData.append("is_approved", true);
          approveFormData.append("status", "APPROVED");
          approveFormData.append("approval_date", currentDate.toISOString());
          approveFormData.append("comment", values.comment);
          approveFormData.append("partner_id", parseInt(partner_id));

          editSalesRequest({
            id: item,
            data: approveFormData,
            status: "Approved",
          }).then(() => {
            navigateBack();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showRejectConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to reject this Request?",
      className: "btn-confirm-red",
      content: showCommentsForm(),
      okText: "Reject",
      cancelText: "Cancel",
      icon: <CloseCircleOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "REJECTED");
          rejectFormData.append("comment", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Rejected",
          }).then(() => {
            navigateBack();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showReviseConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to request for revisions?",
      className: "btn-confirm-primary",
      content: showCommentsForm(),
      okText: "Submit",
      cancelText: "Cancel",
      icon: <ExclamationCircleOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "REVISIONS REQUIRED");
          rejectFormData.append("comment", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Status Sent",
          }).then(() => {
            navigateBack();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showClarificationConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to request for clarification?",
      className: "btn-confirm-primary",
      content: showCommentsForm(),
      okText: "Submit",
      cancelText: "Cancel",
      icon: <WarningOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "NEEDS CLARIFICATION");
          rejectFormData.append("message", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Status Sent",
          }).then(() => {
            navigateBack();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const labelStyle = { width: "200px" };

  if (isRequestLoading) {
    return <Spin tip="Loading..." />;
  }

  return (
    <div style={{ padding: "30px" }}>
      <Col span={24}>
        <div
          className="gx-flex-row"
          style={{ justifyContent: "space-between" }}
        >
          <h1 className="title gx-mb-4">
            <LeftOutlined className="mr-5" onClick={navigateBack} />
            Royalty Sales Request Information
          </h1>
          {isAdminApprover && isUserAdmin && (
            <div>
              <Button
                className="gx-btn-success"
                icon={<CheckOutlined />}
                onClick={() =>
                  showApproveConfirm(
                    salesRequestData.request_id,
                    salesRequestData.partner_id.partner_id,
                  )
                }
                id={`approve-${salesRequestData.request_id}`}
                disabled={isDisabled}
                loading={isEditing}
              >
                Approve
              </Button>

              <Button
                className="gx-btn-yellow"
                onClick={() =>
                  showReviseConfirm(
                    salesRequestData.request_id,
                    salesRequestData.partner_id.partner_id,
                  )
                }
                icon={<ExclamationCircleOutlined />}
                id={`revisions-${salesRequestData.request_id}`}
                disabled={isDisabled}
                loading={isEditing}
              >
                Revisions Required
              </Button>

              <Button
                className="gx-btn-warning"
                onClick={() =>
                  showClarificationConfirm(
                    salesRequestData.request_id,
                    salesRequestData.partner_id.partner_id,
                  )
                }
                icon={<WarningOutlined />}
                id={`clarification-${salesRequestData.request_id}`}
                disabled={salesRequestData.status !== "PENDING"}
                loading={isEditing}
              >
                Needs Clarification
              </Button>

              <Button
                className="gx-btn-danger"
                icon={<CloseOutlined />}
                onClick={() =>
                  showRejectConfirm(
                    salesRequestData.request_id,
                    salesRequestData.partner_id.partner_id,
                  )
                }
                id={`reject-${salesRequestData.request_id}`}
                disabled={isDisabled}
                loading={isEditing}
              >
                Reject
              </Button>
            </div>
          )}
        </div>
      </Col>
      <Col span={12}>
        <Descriptions
          bordered
          column={1}
          style={{
            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
          }}
        >
          <Descriptions.Item label="Company Name" style={labelStyle}>
            {salesRequestData?.partner_id?.company_name}
          </Descriptions.Item>
<Descriptions.Item label="Product Name" style={labelStyle}>
            {salesRequestData?.product_id?.product_name}
          </Descriptions.Item>
          <Descriptions.Item label="Country" style={labelStyle}>
            {salesRequestData?.country_id?.country_name}
          </Descriptions.Item>
          <Descriptions.Item label="Request Date" style={labelStyle}>
            {salesRequestData?.request_date
              ? moment(salesRequestData.request_date).format("DD/MM/YYYY")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Expected Date of Sale" style={labelStyle}>
            {salesRequestData?.expected_date_of_sale
              ? moment(salesRequestData.expected_date_of_sale).format(
                  "DD/MM/YYYY",
                )
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Pack Size" style={labelStyle}>
            {salesRequestData?.pack_size}
          </Descriptions.Item>
          <Descriptions.Item label="Quantity" style={labelStyle}>
            {salesRequestData?.quantity}
          </Descriptions.Item>
          <Descriptions.Item label="Proof of Sale Details" style={labelStyle}>
            {salesRequestData?.proof_of_sale_details}
          </Descriptions.Item>
          <Descriptions.Item label="Buyer Name and Address" style={labelStyle}>
            {salesRequestData?.buyer_name_and_address}
          </Descriptions.Item>
          <Descriptions.Item
            label="End Customer and Address"
            style={labelStyle}
          >
            {salesRequestData?.end_customer_and_address}
          </Descriptions.Item>
          <Descriptions.Item label="Status" style={labelStyle}>
            {salesRequestData?.status}
            {salesRequestData?.clarification?.length > 0 && (
              <Tooltip
                onClick={() => {
                  handleClarificationClick(salesRequestData.clarification);
                }}
              >
                <span
                  style={{
                    cursor: "pointer",
                    paddingLeft: "5px",
                  }}
                >
                  <MessageTwoTone twoToneColor="#00AEEF" className="font-20" />
                </span>
              </Tooltip>
            )}
          </Descriptions.Item>
          {salesRequestData?.comment && (
            <Descriptions.Item label="Comments" style={labelStyle}>
              {salesRequestData.comment}
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Documents" style={labelStyle}>
            {salesRequestData?.file?.map((file, index) => (
              <React.Fragment key={file.po_id || index}>
                <a
                  href={file.po_document}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PO {index + 1}
                </a>
                <br />
              </React.Fragment>
            ))}
          </Descriptions.Item>
        </Descriptions>
        <Modal
          title="Clarification Chat"
          visible={isChatModalVisible}
          onCancel={() => setIsChatModalVisible(false)}
          footer={null}
        >
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <List
              itemLayout="horizontal"
              dataSource={chatConversation}
              renderItem={(message) => (
                <List.Item
                  className={classNames("chat-message", {
                    "chat-message-user": !message.is_partner,
                    "chat-message-partner": message.is_partner,
                  })}
                >
                  <List.Item.Meta
                    description={
                      <div
                        style={{
                          background: message.is_partner
                            ? "#f6f6f6"
                            : "#e6f7ff",
                        }}
                        className="chat-message-content"
                      >
                        {message.message}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
          {salesRequestData?.status === "NEEDS CLARIFICATION" && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 20,
              }}
            >
              <TextArea
                rows={3}
                value={messageInput}
                onChange={handleMessageChange}
                placeholder="Type your message..."
                autoFocus
                style={{ flex: 1, marginRight: 10 }}
              />
              <Button
                type="primary"
                onClick={() => {
                  handleSendMessage(chatConversation[0]?.request_id);
                }}
                loading={isEditing}
              >
                Send
              </Button>
            </div>
          )}
        </Modal>
      </Col>
    </div>
  );
};

export default ViewRaisedRequest;